<script setup lang="ts">
import BaseInputSelect from '../../../base/BaseInputSelect.vue'
import { computed, defineEmits, defineProps, reactive, ref, RenderFunction, watch } from 'vue'
import { AccountDataState, KycDocumentVerificationType, SelectOption } from '../../../../typings'
import { i18n } from '../../../../localization'
import { FormContext, useForm } from 'vee-validate'
import { MinimalKycDocument } from '../../../../utilities/api'
import { CreditCardIcon, UserIcon } from '@heroicons/vue/solid'
import BaseInputText from '../../../base/BaseInputText.vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import BaseKycDocumentFileUpload from '../../../base/BaseKycDocumentFileUpload.vue'
import { useWalletState, Wallet } from '../../../../utilities/account/useWallet'
import { storeToRefs } from 'pinia'
import { useNotificationState } from '../../../../utilities/useNotifications'

const props = defineProps<{
    verificationType: KycDocumentVerificationType
    store: AccountDataState
    icon: RenderFunction
    modelValue: MinimalKycDocument
    form: FormContext | null
    uploading: boolean
    identificationTypes: SelectOption[]
}>()

const uploadType = ref<'image' | 'pdf' | null>(null)
const notificationState = useNotificationState()
const walletStore = useWalletState()
const { wallets } = storeToRefs(walletStore)
const walletSelection = ref('')

const form = useForm({
    validationSchema: {
        'newKycDocument.type': 'required',
        // 'newKycDocument.kyc_document': 'required|requiredArray',
        'newKycDocument.bankAccount.holder':
            props.verificationType === KycDocumentVerificationType.KycBank ? 'required' : undefined, // only fpr kyc_bank
        'newKycDocument.bankAccount.iban':
            props.verificationType === KycDocumentVerificationType.KycBank ? 'required|iban' : undefined, // only fpr kyc_bank
        // 'newKycDocument.bankAccount.bic': 'required',
    },
})
const emit = defineEmits(['update:modelValue', 'update:form', 'update:uploading'])
emit('update:form', form)

const uploading = ref(props.uploading)
watch(uploading, () => emit('update:uploading', uploading.value))

const newKycDocument = reactive<MinimalKycDocument>(props.modelValue)
watch(newKycDocument, () => {
    emit('update:modelValue', { ...newKycDocument, wallet_id: walletSelection.value ?? null })
})

watch(
    () => walletSelection.value,
    () => {
        emit('update:modelValue', { ...newKycDocument, wallet_id: walletSelection.value ?? null })
    }
)

function updateStorageKey(files) {
    if (files.length === 0) {
        newKycDocument.storage_key = null
        uploadType.value = null
    } else {
        newKycDocument.storage_key = files[0]?.key
        uploadType.value = 'image'
    }
}

function updateStorageKeyBack(files) {
    if (files.length === 0) {
        newKycDocument.storage_key_back = null
        uploadType.value = null
    } else {
        newKycDocument.storage_key_back = files[0]?.key
        uploadType.value = 'image'
    }
}

function updateStorageKeyPdf(files) {
    if (files.length === 0) {
        newKycDocument.storage_key_pdf = null
        uploadType.value = null
    } else {
        newKycDocument.storage_key_pdf = files[0]?.key
        uploadType.value = 'pdf'
    }
}

const hasProfileBankData = computed(
    () => props.store.getBankDataFromProfile.holder || props.store.getBankDataFromProfile.iban
)

const profileBankDataisDifferent = computed(
    () =>
        props.store.getBankDataFromProfile.iban &&
        (props.store.getBankDataFromProfile.holder !== newKycDocument.bankAccount.holder ||
            props.store.getBankDataFromProfile.iban !== newKycDocument.bankAccount.iban)
)

function setBankDataFromProfile() {
    const bankData = props.store.getBankDataFromProfile
    newKycDocument.bankAccount.holder = bankData.holder
    newKycDocument.bankAccount.iban = bankData.iban
}

function selectWallet(wallet: Wallet) {
    if (wallet.type === 'lemonway') {
        walletSelection.value = wallet.id
    } else {
        notificationState.addNotification(
            'Error',
            i18n.global.t('error'),
            i18n.global.t('wallet.secupay_verification_not_supported')
        )
        walletSelection.value = ''
    }
}
</script>

<template>
    <div>
        <div v-if="newKycDocument.bankAccount" class="grid grid-cols-1 gap-x-4 gap-y-2 pb-2 sm:grid-cols-2">
            <div class="col-span-2">
                <div class="wallet-scroll">
                    <button
                        v-for="wallet in wallets"
                        :key="wallet.id"
                        :class="[
                            'wallet-card relative flex h-20 flex-col items-center justify-center rounded-md bg-gray-200 text-black',
                            { 'bg-green-500 text-white': walletSelection == wallet.id },
                        ]"
                        @click="selectWallet(wallet)">
                        <p class="text-sm">{{ $t(`wallet.${wallet.type}`) }}</p>
                        <p class="text-sm">{{ wallet.type === 'lemonway' ? wallet.external_id : '' }}</p>
                    </button>
                </div>
            </div>
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="newKycDocument.bankAccount.holder"
                    :icon="UserIcon"
                    name="newKycDocument.bankAccount.holder"
                    :label="$t('verification_kyc_document.account_holder')" />
            </div>
            <div class="sm:col-span-1">
                <BaseInputText
                    v-model="newKycDocument.bankAccount.iban"
                    :icon="CreditCardIcon"
                    name="newKycDocument.bankAccount.iban"
                    :label="$t('account.iban')" />
            </div>

            <div v-if="hasProfileBankData && profileBankDataisDifferent" class="sm:col-span-2">
                <BaseButtonMuted @click="setBankDataFromProfile">{{
                    $t('bank_data.set_bank_data_from_profile')
                }}</BaseButtonMuted>
            </div>

            <!--            Currently not used-->
            <!--            <div class="sm:col-span-1">-->
            <!--                <BaseInputText-->
            <!--                    v-model="newKycDocument.bankAccount.bic"-->
            <!--                    :icon="CreditCardIcon"-->
            <!--                    name="newKycDocument.bankAccount.bic"-->
            <!--                    :label="$t('account.bic')" />-->
            <!--            </div>-->
        </div>
        <BaseInputSelect
            v-model="newKycDocument.type"
            :disabled="identificationTypes.length === 1"
            name="newKycDocument.type"
            rules="required"
            :label="$t('verification_kyc_document.identification_type')"
            :icon="icon"
            :options="identificationTypes">
        </BaseInputSelect>
        <template v-if="props.verificationType === KycDocumentVerificationType.KycId">
            <h4
                v-if="uploadType === 'pdf' || uploadType === null"
                class="mb-2 mt-6 text-base font-medium leading-6 text-gray-900">
                {{ $t('verification.pdf_both_sides') }}
            </h4>
            <BaseKycDocumentFileUpload
                v-if="uploadType === 'pdf' || uploadType === null"
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document_pdf"
                class="my-2"
                :required="false"
                :required2="false"
                :camera="false"
                :accepted-file-types="['application/pdf']"
                max-file-size="5MB"
                @update:model-value="updateStorageKeyPdf" />
            <div v-if="uploadType === null" class="separator my-16 text-sm text-gray-400">
                {{ $t('camera.or') }}
            </div>
            <h4
                v-if="uploadType === 'image' || uploadType === null"
                class="mb-2 mt-10 text-base font-medium leading-6 text-gray-900">
                {{ $t('id_front') }}
            </h4>
            <BaseKycDocumentFileUpload
                v-if="uploadType === 'image' || uploadType === null"
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document"
                class="my-2"
                :camera="true"
                :required="false"
                :required2="false"
                max-file-size="5MB"
                @update:model-value="updateStorageKey" />

            <h4
                v-if="uploadType === 'image' || uploadType === null"
                class="mb-2 mt-6 text-base font-medium leading-6 text-gray-900">
                {{ $t('id_back') }}
            </h4>
            <BaseKycDocumentFileUpload
                v-if="uploadType === 'image' || uploadType === null"
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document_back"
                class="my-2"
                :camera="true"
                :required="false"
                :required2="false"
                max-file-size="5MB"
                @update:model-value="updateStorageKeyBack" />
        </template>
        <template v-else>
            <BaseKycDocumentFileUpload
                v-model:uploading="uploading"
                name="newKycDocument.kyc_document"
                class="my-2"
                :camera="true"
                :accepted-file-types="['image/jpg', 'image/jpeg', 'image/png', 'application/pdf']"
                max-file-size="5MB"
                @update:model-value="updateStorageKey" />
        </template>
    </div>
</template>

<style scoped>
.separator {
    display: flex;
    align-items: center;
    text-align: center;
    margin-bottom: 0.6em;
}

.separator::before,
.separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #d1d5db;
}

.separator:not(:empty)::before {
    margin-right: 0.25em;
}

.separator:not(:empty)::after {
    margin-left: 0.25em;
}

.wallet-scroll {
    display: flex;
    gap: 0.75rem;
    overflow-x: auto;
    padding: 0.5rem 0;
    scroll-snap-type: x proximity;
}

.wallet-card {
    flex: 0 0 45%;
    min-width: 45%;
    scroll-snap-align: start;
}
</style>
